import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import RealtorEditModal from "./RealtorEditModal";

const STATUS_META = {
  active: { label: "Active", color: "text-emerald-400 bg-emerald-500/10" },
  pending: { label: "Pending", color: "text-amber-400 bg-amber-500/10" },
  sold: { label: "Sold", color: "text-zinc-400 bg-zinc-500/10" },
  draft: { label: "Draft", color: "text-blue-400 bg-blue-500/10" },
};
const STATUS_KEYS = Object.keys(STATUS_META);

/**
 * Sidebar with hierarchy: Company → Realtor → Listing.
 * Calls onSelect(listingId, listing, realtor, company) when a listing is picked.
 */
export default function CrmSidebar({ activeListingId, onSelect }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [realtorsBy, setRealtorsBy] = useState({}); // company_id -> realtors
  const [listingsBy, setListingsBy] = useState({}); // realtor_id -> listings
  const [openCompanies, setOpenCompanies] = useState({});
  const [openRealtors, setOpenRealtors] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null = all
  const [editingRealtor, setEditingRealtor] = useState(null);

  // Form state for inline create
  const [newCo, setNewCo] = useState("");
  const [newRtIn, setNewRtIn] = useState({}); // company_id -> name
  const [newLnIn, setNewLnIn] = useState({}); // realtor_id -> address

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch {}
  };
  const loadRealtors = async (cid) => {
    try {
      const { data } = await api.get(`/realtors?company_id=${cid}`);
      setRealtorsBy((m) => ({ ...m, [cid]: data }));
    } catch {}
  };
  const loadListings = async (rid) => {
    try {
      const { data } = await api.get(`/listings?realtor_id=${rid}`);
      setListingsBy((m) => ({ ...m, [rid]: data }));
    } catch {}
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const toggleCompany = (cid) => {
    setOpenCompanies((m) => ({ ...m, [cid]: !m[cid] }));
    if (!realtorsBy[cid]) loadRealtors(cid);
  };
  const toggleRealtor = (rid) => {
    setOpenRealtors((m) => ({ ...m, [rid]: !m[rid] }));
    if (!listingsBy[rid]) loadListings(rid);
  };

  const createCompany = async () => {
    if (!newCo.trim()) return;
    try {
      await api.post("/companies", { name: newCo.trim() });
      setNewCo("");
      await loadCompanies();
      toast.success("Company added");
    } catch {
      toast.error("Could not add company");
    }
  };
  const createRealtor = async (cid) => {
    const name = (newRtIn[cid] || "").trim();
    if (!name) return;
    try {
      await api.post("/realtors", { company_id: cid, name });
      setNewRtIn((m) => ({ ...m, [cid]: "" }));
      await loadRealtors(cid);
      await loadCompanies();
      setOpenCompanies((m) => ({ ...m, [cid]: true }));
    } catch {
      toast.error("Could not add realtor");
    }
  };
  const createListing = async (rid) => {
    const address = (newLnIn[rid] || "").trim();
    if (!address) return;
    try {
      await api.post("/listings", { realtor_id: rid, address });
      setNewLnIn((m) => ({ ...m, [rid]: "" }));
      await loadListings(rid);
      setOpenRealtors((m) => ({ ...m, [rid]: true }));
    } catch {
      toast.error("Could not add listing");
    }
  };

  const removeCompany = async (cid) => {
    if (!confirm("Delete this company and all realtors/listings under it?")) return;
    await api.delete(`/companies/${cid}`);
    await loadCompanies();
    if (activeListingId) onSelect(null);
  };
  const removeRealtor = async (rid, cid) => {
    if (!confirm("Delete this realtor and all their listings?")) return;
    await api.delete(`/realtors/${rid}`);
    await loadRealtors(cid);
    await loadCompanies();
    if (activeListingId) onSelect(null);
  };
  const removeListing = async (lid, rid) => {
    if (!confirm("Delete this listing?")) return;
    await api.delete(`/listings/${lid}`);
    await loadListings(rid);
    if (activeListingId === lid) onSelect(null);
  };

  const setListingUrl = async (lid, rid) => {
    const current = (listingsBy[rid] || []).find((l) => l.id === lid)?.listing_url || "";
    const url = window.prompt(
      "Paste the public listing URL (e.g. realtor.ca link):",
      current
    );
    if (url === null) return; // cancelled
    try {
      await api.patch(`/listings/${lid}`, { listing_url: url.trim() || null });
      await loadListings(rid);
      toast.success(url.trim() ? "Listing URL saved" : "Listing URL cleared");
    } catch {
      toast.error("Could not save listing URL");
    }
  };

  const setCompanyColor = async (cid, color) => {
    try {
      await api.patch(`/companies/${cid}`, { color });
      await loadCompanies();
    } catch {
      toast.error("Could not update brand color");
    }
  };

  const uploadCompanyLogo = async (cid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "logo"); // optional hint; endpoint ignores extras
    try {
      const { data } = await api.post("/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch(`/companies/${cid}`, { logo_image_id: data.id });
      await loadCompanies();
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  const clearCompanyLogo = async (cid) => {
    try {
      await api.patch(`/companies/${cid}`, { logo_image_id: null });
      await loadCompanies();
    } catch {}
  };

  const setListingStatus = async (lid, rid, status) => {
    try {
      await api.patch(`/listings/${lid}`, { status });
      await loadListings(rid);
    } catch {
      toast.error("Could not update status");
    }
  };

  const cycleStatus = (current) => {
    const idx = STATUS_KEYS.indexOf(current || "active");
    return STATUS_KEYS[(idx + 1) % STATUS_KEYS.length];
  };

  const editRealtor = async (realtorId) => {
    try {
      const list = realtorsBy[Object.keys(realtorsBy).find((cid) =>
        (realtorsBy[cid] || []).some((r) => r.id === realtorId)
      )] || [];
      const r = list.find((x) => x.id === realtorId);
      // Need full data — fetch single realtor list to get latest fields
      setEditingRealtor(r);
    } catch {}
  };

  const copyDashboardLink = async (rid) => {
    try {
      const { data } = await api.get(`/realtors/${rid}/dashboard-token`);
      const url = `${window.location.origin}/r/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Dashboard link copied", { description: url });
      } catch {
        // Fallback if clipboard API blocked
        window.prompt("Copy this dashboard link:", url);
      }
    } catch {
      toast.error("Could not generate dashboard link");
    }
  };

  const lower = search.trim().toLowerCase();
  const matchesSearch = (s) => !lower || s.toLowerCase().includes(lower);

  const filteredCompanies = companies.filter((c) => {
    if (!lower) return true;
    if (matchesSearch(c.name)) return true;
    const realtors = realtorsBy[c.id] || [];
    return realtors.some(
      (r) =>
        matchesSearch(r.name) ||
        (listingsBy[r.id] || []).some((l) => matchesSearch(l.address))
    );
  });

  return (
    <aside className="w-96 shrink-0 border-r border-white/10 bg-[#070707] flex flex-col h-[calc(100vh-3.5rem)] sticky top-14">
      <div className="p-3 border-b border-white/10 space-y-2">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          CRM
        </div>
        <div className="relative">
          <Icons.Search
            size={12}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search realtors, addresses…"
            className="w-full bg-white/5 border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
            data-testid="crm-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setStatusFilter(null)}
            className={`text-[9px] uppercase tracking-wider font-mono px-2 py-1 rounded transition-all ${
              !statusFilter
                ? "bg-white text-black"
                : "bg-white/5 text-zinc-400 hover:text-white"
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          {STATUS_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`text-[9px] uppercase tracking-wider font-mono px-2 py-1 rounded transition-all ${
                statusFilter === k
                  ? "bg-white text-black"
                  : `${STATUS_META[k].color} hover:brightness-150`
              }`}
              data-testid={`filter-${k}`}
            >
              {STATUS_META[k].label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-3 space-y-2">
        {filteredCompanies.length === 0 && (
          <div className="text-xs text-zinc-600 italic px-1 py-3">
            No companies yet. Add one below to start organizing.
          </div>
        )}

        {filteredCompanies.map((c) => {
          const open = openCompanies[c.id];
          const realtors = realtorsBy[c.id] || [];
          return (
            <div key={c.id} className="space-y-1">
              <div className="group flex items-center gap-1">
                <button
                  onClick={() => toggleCompany(c.id)}
                  className="flex-1 flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-white/5 transition-all"
                  data-testid={`crm-company-${c.id}`}
                >
                  <Icons.ChevronRight
                    size={12}
                    className={`text-zinc-500 transition-transform ${
                      open ? "rotate-90" : ""
                    }`}
                  />
                  <Icons.Building2 size={12} className="text-zinc-400" />
                  <span className="text-xs text-white font-medium truncate">
                    {c.name}
                  </span>
                  <span className="text-[10px] text-zinc-600 font-mono ml-auto">
                    {c.realtor_count}
                  </span>
                </button>
                <label
                  className="relative w-4 h-4 rounded-full cursor-pointer border border-white/20 hover:border-white/40 shrink-0"
                  style={{ background: c.color || "transparent" }}
                  title={`Brand color${c.color ? `: ${c.color}` : " (click to set)"}`}
                  data-testid={`crm-company-color-${c.id}`}
                >
                  {!c.color && (
                    <Icons.Palette
                      size={10}
                      className="absolute inset-0 m-auto text-zinc-500"
                    />
                  )}
                  <input
                    type="color"
                    value={c.color || "#ffffff"}
                    onChange={(e) => setCompanyColor(c.id, e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
                <label
                  className="relative w-4 h-4 rounded cursor-pointer border border-white/20 hover:border-white/40 shrink-0 overflow-hidden"
                  title={c.logo_image_id ? "Click to replace logo" : "Upload brand logo"}
                  data-testid={`crm-company-logo-${c.id}`}
                >
                  {c.logo_image_id ? (
                    <img
                      src={mediaUrl(`/api/public/images/${c.logo_image_id}`)}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain bg-white"
                    />
                  ) : (
                    <Icons.ImagePlus
                      size={10}
                      className="absolute inset-0 m-auto text-zinc-500"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadCompanyLogo(c.id, f);
                      e.target.value = "";
                    }}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
                {c.logo_image_id && (
                  <button
                    onClick={() => clearCompanyLogo(c.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-600 hover:text-red-400"
                    title="Remove logo"
                  >
                    <Icons.X size={9} />
                  </button>
                )}
                <button
                  onClick={() => removeCompany(c.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-zinc-600 hover:text-red-400"
                  data-testid={`crm-company-delete-${c.id}`}
                >
                  <Icons.Trash2 size={11} />
                </button>
              </div>

              {open && (
                <div className="ml-4 space-y-1 border-l border-white/5 pl-2">
                  {realtors.map((r) => {
                    const ropen = openRealtors[r.id];
                    const listings = listingsBy[r.id] || [];
                    return (
                      <div key={r.id} className="space-y-0.5">
                        <div className="group flex items-center gap-1">
                          <button
                            onClick={() => toggleRealtor(r.id)}
                            className="flex-1 flex items-center gap-2 text-left px-2 py-1 rounded hover:bg-white/5"
                            data-testid={`crm-realtor-${r.id}`}
                          >
                            <Icons.ChevronRight
                              size={10}
                              className={`text-zinc-500 transition-transform ${
                                ropen ? "rotate-90" : ""
                              }`}
                            />
                            <Icons.User size={10} className="text-zinc-400" />
                            <span className="text-xs text-zinc-300 truncate">
                              {r.name}
                            </span>
                            <span className="text-[10px] text-zinc-600 font-mono ml-auto">
                              {r.listing_count}
                            </span>
                          </button>
                          <button
                            onClick={() => setEditingRealtor(r)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-zinc-600 hover:text-white"
                            title="Edit realtor profile"
                            data-testid={`crm-realtor-edit-${r.id}`}
                          >
                            <Icons.Settings size={10} />
                          </button>
                          <button
                            onClick={() => copyDashboardLink(r.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-zinc-600 hover:text-white"
                            title="Copy realtor dashboard link"
                            data-testid={`crm-realtor-dashlink-${r.id}`}
                          >
                            <Icons.Link2 size={10} />
                          </button>
                          <button
                            onClick={() => removeRealtor(r.id, c.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-zinc-600 hover:text-red-400"
                          >
                            <Icons.Trash2 size={10} />
                          </button>
                        </div>

                        {ropen && (
                          <div className="ml-4 space-y-0.5 border-l border-white/5 pl-2">
                            {listings
                              .filter(
                                (l) =>
                                  !statusFilter ||
                                  (l.status || "active") === statusFilter
                              )
                              .map((l) => {
                              const status = l.status || "active";
                              const meta = STATUS_META[status];
                              return (
                              <div
                                key={l.id}
                                className={`group flex items-start gap-1 ${
                                  activeListingId === l.id
                                    ? "bg-white/10 rounded"
                                    : ""
                                }`}
                              >
                                <button
                                  onClick={() => onSelect(l.id, l, r, c)}
                                  className={`flex-1 min-w-0 flex items-start gap-2 text-left px-2 py-1 rounded hover:bg-white/5 ${
                                    activeListingId === l.id
                                      ? "text-white"
                                      : "text-zinc-400"
                                  }`}
                                  data-testid={`crm-listing-${l.id}`}
                                >
                                  {l.cover_image_id ? (
                                    <img
                                      src={mediaUrl(`/api/public/images/${l.cover_image_id}`)}
                                      alt=""
                                      className="w-6 h-6 rounded object-cover shrink-0 mt-0.5"
                                    />
                                  ) : (
                                    <Icons.Home
                                      size={10}
                                      className={`mt-1 shrink-0 ${
                                        activeListingId === l.id
                                          ? "text-white"
                                          : "text-zinc-500"
                                      }`}
                                    />
                                  )}
                                  <span className="text-[11px] flex-1 min-w-0 break-words leading-snug">
                                    {l.address}
                                  </span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setListingStatus(l.id, r.id, cycleStatus(status));
                                  }}
                                  className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded ${meta.color}`}
                                  title={`Status: ${meta.label} (click to cycle)`}
                                  data-testid={`crm-listing-status-${l.id}`}
                                >
                                  {meta.label}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setListingUrl(l.id, r.id);
                                  }}
                                  className={`opacity-0 group-hover:opacity-100 p-1 ${
                                    l.listing_url
                                      ? "text-emerald-400 opacity-100"
                                      : "text-zinc-600 hover:text-zinc-300"
                                  }`}
                                  title={
                                    l.listing_url
                                      ? `Public URL: ${l.listing_url}`
                                      : "Add public listing URL (e.g. realtor.ca)"
                                  }
                                  data-testid={`crm-listing-url-${l.id}`}
                                >
                                  <Icons.Link size={10} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/listings/${l.id}`);
                                  }}
                                  className={`opacity-0 group-hover:opacity-100 p-1 ${
                                    l.stats?.price
                                      ? "text-amber-400 opacity-100"
                                      : "text-zinc-600 hover:text-zinc-300"
                                  }`}
                                  title={
                                    l.stats?.price
                                      ? "Open property details"
                                      : "Open property details (add price, beds, baths…)"
                                  }
                                  data-testid={`crm-listing-stats-${l.id}`}
                                >
                                  <Icons.Tag size={10} />
                                </button>
                                <button
                                  onClick={() => removeListing(l.id, r.id)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-zinc-600 hover:text-red-400"
                                >
                                  <Icons.Trash2 size={10} />
                                </button>
                              </div>
                              );
                            })}

                            {/* New listing input */}
                            <div className="flex items-center gap-1 mt-1">
                              <input
                                value={newLnIn[r.id] || ""}
                                onChange={(e) =>
                                  setNewLnIn((m) => ({
                                    ...m,
                                    [r.id]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) =>
                                  e.key === "Enter" && createListing(r.id)
                                }
                                placeholder="+ listing address"
                                className="flex-1 bg-transparent border border-dashed border-white/10 hover:border-white/20 rounded px-2 py-1 text-[11px] text-zinc-400 placeholder:text-zinc-700 focus:outline-none focus:border-white/40"
                                data-testid={`crm-new-listing-${r.id}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* New realtor input */}
                  <div className="flex items-center gap-1">
                    <input
                      value={newRtIn[c.id] || ""}
                      onChange={(e) =>
                        setNewRtIn((m) => ({ ...m, [c.id]: e.target.value }))
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && createRealtor(c.id)
                      }
                      placeholder="+ realtor name"
                      className="flex-1 bg-transparent border border-dashed border-white/10 hover:border-white/20 rounded px-2 py-1 text-[11px] text-zinc-400 placeholder:text-zinc-700 focus:outline-none focus:border-white/40"
                      data-testid={`crm-new-realtor-${c.id}`}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-1">
          <input
            value={newCo}
            onChange={(e) => setNewCo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createCompany()}
            placeholder="+ Add company"
            className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
            data-testid="crm-new-company"
          />
          <button
            onClick={createCompany}
            disabled={!newCo.trim()}
            className="bg-white text-black rounded px-2 py-1.5 disabled:opacity-30"
            data-testid="crm-new-company-submit"
          >
            <Icons.Plus size={12} />
          </button>
        </div>
      </div>

      <RealtorEditModal
        realtor={editingRealtor}
        onClose={() => setEditingRealtor(null)}
        onSaved={() => {
          if (editingRealtor)
            loadRealtors(editingRealtor.company_id);
        }}
      />
    </aside>
  );
}
